import { nanoid } from "nanoid";
import {
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { AIProviderId } from "../../shared/aiProviders";
import {
  attachmentMediaType,
  IMAGE_TYPES,
  maxImageDimensionFor,
  volumeAttachmentSizeLimit,
} from "../../shared/attachmentPolicy";
import {
  attachmentSizeLimit,
  type PendingAttachment,
  resolveAttachmentMediaType,
  UploadError,
  uploadFile,
} from "../lib/attachments";
import { attachmentUploadPath } from "../lib/apiPaths";
import { listZip } from "../lib/archive";
import { emitErrorToast } from "../lib/errorToast";
import { fitImage } from "../lib/imageProcessing";

function isZip(file: Pick<File, "name">) {
  return file.name.toLowerCase().endsWith(".zip");
}

async function isWorkflowExport(zip: File) {
  try {
    const entries = listZip(new Uint8Array(await zip.arrayBuffer()));
    return entries.some((e) => e.path.endsWith("/config.toml"));
  } catch {
    // An unreadable ZIP goes to import, which reports why it can’t be read.
    return true;
  }
}

// A canvas re-encode would keep only a GIF's first frame.
function isResizableImage(mediaType: string) {
  return IMAGE_TYPES.has(mediaType) && mediaType !== "image/gif";
}

function rejectionToast(message: string) {
  emitErrorToast({ icon: "paperclip", message, buttonLabel: "Dismiss" });
}

function mediaTypeFor(
  file: File,
  providerId: AIProviderId,
  acceptAnyFile: boolean
) {
  return acceptAnyFile
    ? attachmentMediaType(file)
    : resolveAttachmentMediaType(file, providerId);
}

function sizeLimitFor(mediaType: string, acceptAnyFile: boolean) {
  return acceptAnyFile
    ? volumeAttachmentSizeLimit(mediaType)
    : attachmentSizeLimit(mediaType);
}

function sizeLimitLabel(mediaType: string, max: number): string {
  const limitMb = Math.floor((max / (1024 * 1024)) * 10) / 10;
  const kind = IMAGE_TYPES.has(mediaType)
    ? "Images"
    : mediaType === "application/pdf"
      ? "PDFs"
      : "Files of this type";
  return `${kind} must be under ${limitMb} MB.`;
}

function tooLargeToast(
  fileName: string,
  sizeBytes: number,
  mediaType: string,
  limit: number
) {
  const actualMb = Math.round((sizeBytes / (1024 * 1024)) * 10) / 10;
  rejectionToast(
    `${fileName} is too large (${actualMb} MB). ${sizeLimitLabel(mediaType, limit)}`
  );
}

export function useFileDropzone({
  enabled = true,
  providerId,
  onZipFile,
  onZipAttachment,
  acceptAnyFile = false,
}: {
  enabled?: boolean;
  providerId: AIProviderId;
  onZipFile?: (file: File, attach: () => void) => void;
  onZipAttachment?: () => void;
  acceptAnyFile?: boolean;
}) {
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingAttachment[]
  >([]);
  const pendingRef = useRef(pendingAttachments);
  useEffect(() => {
    pendingRef.current = pendingAttachments;
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);
  const [flyingFiles, setFlyingFiles] = useState<
    { id: string; x: number; y: number }[]
  >([]);

  const removeAttachment = (id: string) => {
    setPendingAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const patchAttachment = (id: string, patch: Partial<PendingAttachment>) => {
    setPendingAttachments((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...patch } : a))
    );
  };

  const uploadAttachment = async (
    id: string,
    file: File,
    sourceMediaType: string
  ) => {
    const limit = sizeLimitFor(
      sourceMediaType,
      acceptAnyFile ||
        (!!onZipAttachment && sourceMediaType === "application/zip")
    );
    let blob: Blob = file;
    let mediaType = sourceMediaType;
    if (isResizableImage(sourceMediaType)) {
      const fitted = await fitImage(file, {
        maxDimension: maxImageDimensionFor(providerId),
        maxBytes: limit,
        mediaType: sourceMediaType,
      });
      if (fitted) {
        blob = fitted.blob;
        mediaType = fitted.mediaType;
      }
    }
    if (blob.size > limit) {
      removeAttachment(id);
      tooLargeToast(file.name, blob.size, sourceMediaType, limit);
      return;
    }
    const remoteId = await uploadFile(attachmentUploadPath(), blob, mediaType);
    patchAttachment(id, { remoteId, mediaType, sizeBytes: blob.size });
  };

  const attachZipRef = useRef<((file: File) => void) | null>(null);

  const processFiles = async (files: File[], attachWorkflowZip = false) => {
    if (!enabled) return;
    let rest = files;
    const zip = !attachWorkflowZip && onZipFile && files.find(isZip);
    if (zip && (await isWorkflowExport(zip))) {
      onZipFile(zip, () => attachZipRef.current?.(zip));
      rest = files.filter((f) => f !== zip);
    }
    const accepted: PendingAttachment[] = [];
    for (const file of rest) {
      const zipAttachment = !!onZipAttachment && isZip(file);
      const mediaType = mediaTypeFor(
        file,
        providerId,
        acceptAnyFile || zipAttachment
      );
      if (!mediaType) {
        rejectionToast(
          `${file.name} can’t be attached — that file type isn’t supported.`
        );
        continue;
      }
      // Oversized resizable images are shrunk in uploadAttachment, not refused.
      const limit = sizeLimitFor(mediaType, acceptAnyFile || zipAttachment);
      if (!isResizableImage(mediaType) && file.size > limit) {
        tooLargeToast(file.name, file.size, mediaType, limit);
        continue;
      }
      const isDuplicate = [...pendingRef.current, ...accepted].some(
        (a) =>
          a.fileName === file.name &&
          a.sizeBytes === file.size &&
          a.mediaType === mediaType
      );
      if (isDuplicate) continue;
      const id = nanoid(12);
      void uploadAttachment(id, file, mediaType).catch((error) => {
        rejectionToast(
          error instanceof UploadError &&
            (error.status === 413 || error.status === 415)
            ? `${file.name} couldn’t be uploaded. ${error.message}`
            : `${file.name} couldn’t be uploaded.`
        );
        removeAttachment(id);
      });
      accepted.push({
        id,
        fileName: file.name,
        mediaType,
        sizeBytes: file.size,
        blob: file,
        remoteId: null,
      });
    }
    if (accepted.length === 0) return;
    setPendingAttachments((prev) => [...prev, ...accepted]);
    if (accepted.some((attachment) => isZip({ name: attachment.fileName }))) {
      onZipAttachment?.();
    }
  };

  useLayoutEffect(() => {
    attachZipRef.current = (file) => void processFiles([file], true);
    return () => {
      attachZipRef.current = null;
    };
  });

  const isUploading = pendingAttachments.some((a) => a.remoteId == null);

  const clearAttachments = () => {
    setPendingAttachments([]);
  };

  const processDroppedFiles = useEffectEvent((files: File[]) => {
    void processFiles(files);
  });

  useEffect(() => {
    if (!enabled) return;
    const onDragEnter = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer?.types.includes("Files")) {
        dragCounterRef.current++;
        if (dragCounterRef.current === 1) setIsDragging(true);
      }
    };
    const onDragLeave = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current--;
      if (dragCounterRef.current === 0) setIsDragging(false);
    };
    const onDragOver = (e: DragEvent) => e.preventDefault();
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsDragging(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      const hasValid = files.some(
        (f) => mediaTypeFor(f, providerId, acceptAnyFile) != null
      );
      if (hasValid) {
        setFlyingFiles((prev) => [
          ...prev,
          { id: nanoid(6), x: e.clientX, y: e.clientY },
        ]);
      }
      processDroppedFiles(files);
    };
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("drop", onDrop);
    };
  }, [enabled, providerId, acceptAnyFile]);

  const removeFlyingFile = (id: string) => {
    setFlyingFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const files: File[] = [];
    for (const item of items) {
      if (item.kind !== "file") continue;
      const file = item.getAsFile();
      if (!file) continue;
      if (
        mediaTypeFor(file, providerId, acceptAnyFile) != null ||
        ((onZipFile || onZipAttachment) && isZip(file))
      ) {
        files.push(file);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      void processFiles(files);
    }
  };

  return {
    pendingAttachments,
    isUploading,
    isDragging,
    flyingFiles,
    processFiles,
    removeAttachment,
    clearAttachments,
    removeFlyingFile,
    handlePaste,
  };
}
